import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext, AuthContextType, AuthState } from "./authContext";
import { NotificationContext } from "@/common/notification";
import {
	app as microsoftApp,
	authentication as microsoftAuth,
} from "@microsoft/teams-js";
import { Lockr } from "@/common/Lockr";
import {
	getBrowserSessionIdOrCreate,
	cleanTeamsTokens,
	storeLoginData,
	storeUserInfo,
	tryGetTokens,
	embededInTeams,
} from "./authenticationUtils";
import { StorageKeys } from "@/common/constants/storageKeys";
import { signInAzureAD, verifyUser, signOutFn } from "@/repository/queryFns";
import { SignInResult, ResponseStatus, Response } from "@/repository/models";
import { captureError, setSentryUserDetails } from "../helpers/sentryHelper";
import { useIntl } from "react-intl";
import { unifyAuthScopes, authenticateMsal, MsalInstance } from "./msalConfig";
import { AccountInfo } from "@azure/msal-browser";
import { PersistentStorageKey } from "../stores/persistingStores/common";
import { useQueryClient } from "@tanstack/react-query";
import { tryGetUserIdFromJwt } from "./jwtDecoder";
import { useMessage } from "../messages";
import {
	DebugInfoActions,
	DebugInfoKey,
	DebugInfoState,
	useDebugStore,
} from "../stores/persistingStores/debugStore";
import { AxiosError } from "axios";

export const AuthenticationProvider = (props: any) => {
	const intl = useIntl();
	const notification = useContext(NotificationContext);
	const [authState, setAuthState] = useState<AuthState>({
		isLogged: false,
		isInProgress: true,
	} as AuthState);
	const { show: showMessage } = useMessage();
	const debugInfoStore = useDebugStore(
		(state: DebugInfoState) => state.debugInfo
	);
	const debugInfoActions = useDebugStore((state: DebugInfoActions) => state);

	const queryClient = useQueryClient();

	const signInErrorMessageKey = "ErrorMessage.SignInAzureAD";

	useEffect(() => {
		if (embededInTeams) {
			microsoftApp
				.initialize()
				.catch(() => setAuthState((prev) => ({ ...prev, isInProgress: false })))
				.finally(() => updateFromMicrosoftContext());
		} else {
			const activeAccount = MsalInstance.getActiveAccount();
			if (activeAccount != null) {
				trySignInFromStoredToken(activeAccount.localAccountId);
			} else {
				signOutCallback();
			}
		}
	}, []);

	const signOutCallback = useCallback(
		(userId?: string, signOutMessage?: string) => {
			const tokens = tryGetTokens();

			if (!tokens) {
				setAuthState((prev) => ({
					...prev,
					isLogged: false,
					isInProgress: false,
				}));

				return;
			}

			setAuthState((prev) => ({
				...prev,
				isLogoutInProgress: true,
			}));

			signOutFn()
				.catch((err: any) => {
					setAuthState((prev) => ({
						...prev,
						isLogged: false,
						isInProgress: false,
						isLogoutInProgress: false,
					}));

					captureError(err, "Error during logout", { userId });
					console.error("Error during logout:", err);
				})
				.finally(() => cleanAfterSignOut(signOutMessage));
		},
		[]
	);

	//#warning-ts: check if this method is dead code; it is not clear how it could return true
	const hasUserChanged = (userInfo?: microsoftApp.UserInfo): boolean => {  
		const storedUserId = Lockr.get(StorageKeys.MsUserObjectId);
		const tokenId = Lockr.get(StorageKeys.TokenId);
		const tokenUserId = tryGetUserIdFromJwt(tokenId);

		return [storedUserId, tokenUserId].some((id) => id && id !== userInfo?.id);
	};

	const updateFromMicrosoftContext = () => {
		microsoftApp.getContext().then((context: microsoftApp.Context) => {
			const userInfo = context.user;

			if (hasUserChanged(userInfo)) {
				signOutCallback(userInfo?.id);

				return;
			}

			if (!!userInfo) {
				storeUserInfo(userInfo);
				trySignInFromStoredToken(userInfo.id);
			}
		});
	};

	const setCurrentAuthState = (userId?: string) => {
		const tenantId = Lockr.get(StorageKeys.TenantId);
		const upn = Lockr.get(StorageKeys.SIP);

		if (!tenantId || !upn || !userId) {
			setAuthState((prev) => ({
				...prev,
				isLogged: false,
				isInProgress: false,
			}));

			return;
		}

		setAuthState((prev) => ({
			...prev,
			tenantId: tenantId,
			userId: userId,
			upn: upn,
			isInProgress: false,
			isLogged: true,
		}));
	};

	const trySignInFromStoredToken = (userId?: string) => {
		const tokens = tryGetTokens();

		if (!tokens) {
			setAuthState((prev) => ({
				...prev,
				isLogged: false,
				isInProgress: false,
			}));

			return;
		}

		setCurrentAuthState(userId);

		verifyUser(JSON.stringify(debugInfoStore))
			.then((response: Response<SignInResult>) => {
				debugInfoActions.removeDebugInfoItems([
					DebugInfoKey.WindowReloadTriggerEvents,
				]);
				const loginData = response.data;
				if (!!loginData) {
					initClientWithLoginData(loginData);
				} else {
					let signOutMessage: string | undefined = undefined;

					if (response.result === ResponseStatus.SIGNEDOUTBYSUPERVISOR) {
						signOutMessage = intl.formatMessage({
							id: "Account.SignOutBySupervisor",
						});
					}

					signOutCallback(userId, signOutMessage);
				}
			})
			.catch((error: any) => {
				captureError(error, "Verify user failed", { userId });

				if ((error as AxiosError)?.code !== "ERR_NETWORK") {
					debugInfoActions.removeDebugInfoItems([
						DebugInfoKey.WindowReloadTriggerEvents,
					]);
					cleanAfterSignOut();
				}
				//#warning-ts: should log the else branch somehow
			});
	};

	const initClientWithLoginData = (loginData: SignInResult) => {
		storeLoginData(loginData);

		setSentryUserDetails(loginData);

		setAuthState((prev) => ({
			...prev,
			tenantId: loginData.tenantId,
			userId: loginData.userId,
			upn: loginData.upn,
			isInProgress: false,
			isLogged: true,
		}));

		notification.initialize().then(() => {
			notification.joinDefaultRooms();
			notification.notifyAllViewsAtLogin(
				loginData.userId,
				getBrowserSessionIdOrCreate()
			);
		});
	};

	const cleanAfterSignOut = (signOutMessage?: string) => {
		cleanTeamsTokens();

		MsalInstance.setActiveAccount(null);

		notification.leaveDefaultRooms();

		// Clear react query cache
		queryClient.clear();

		Lockr.flush();

		localStorage.removeItem(PersistentStorageKey.Permissions);

		if (!microsoftApp.isInitialized()) {
			localStorage.removeItem(StorageKeys.TIDForAzure);
			localStorage.removeItem(StorageKeys.UPN);
			localStorage.removeItem(StorageKeys.UPNForAzure);
		}

		setAuthState((prev) => ({
			...prev,
			isLogged: false,
			isInProgress: false,
			isLogoutInProgress: false,
		}));

		if (signOutMessage) {
			setTimeout(() => {
				showMessage("global", {
					intent: "warning",
					text: signOutMessage,
					duration: 86400000,
				});
			}, 500);
		}
	};

	const selectAccount = async (): Promise<AccountInfo | undefined> => {
		const loginResponse = await MsalInstance.loginPopup({
			scopes: ["User.Read"],
			prompt: "select_account",
		});

		if (!loginResponse || !loginResponse.account) {
			return undefined;
		}

		MsalInstance.setActiveAccount(loginResponse.account);

		return loginResponse.account;
	};

	const msalSignIn = async (): Promise<boolean> => {
		try {
			const account = await selectAccount();

			if (!account) {
				throw new Error(intl.formatMessage({ id: signInErrorMessageKey }));
			}

			storeUserInfo({
				id: account.localAccountId,
				userPrincipalName: account.username,
				tenant: { id: account.tenantId },
			});

			return true;
		} catch (err: any) {
			captureError(err, "Azure signin failed");
			signOutCallback();
		}

		return false;
	};

	const azureSignIn = async (tenantId: string) => {
		try {
			let token = "";

			if (microsoftApp.isInitialized()) {
				token = await microsoftAuth.getAuthToken({
					claims: [],
					resources: [],
					silent: false,
				});
			} else {
				const authResult = await authenticateMsal({
					scopes: unifyAuthScopes,
				});

				if (!authResult) {
					throw new Error(intl.formatMessage({ id: signInErrorMessageKey }));
				}

				token = authResult.idToken;
			}

			if (!token) {
				throw new Error(intl.formatMessage({ id: signInErrorMessageKey }));
			}

			// try sign in with one retry if first request goes on a wrong env
			const response = await trySignIn(false, token, tenantId);
			const validations = response.validationFailures;

			if (
				(response.result === ResponseStatus.VALIDATIONERROR ||
					response.result === ResponseStatus.SignInWrongEnvironment) &&
				!!validations
			) {
				const message = validations
					.map((x) => intl.formatMessage({ id: x.message }))
					.join(" ");
				signOutCallback();

				throw new Error(message);
			}

			if (!!response.data) {
				initClientWithLoginData(response.data);
			}

			localStorage.setItem(
				StorageKeys.DeploymentTenantId,
				response.data.tenantId
			);

			return response.data;
		} catch (error: any) {
			captureError(error, "Azure signin failed");
			signOutCallback();

			if ((error as AxiosError)?.code !== "ERR_NETWORK") {
				debugInfoActions.removeDebugInfoItems([
					DebugInfoKey.WindowReloadTriggerEvents,
				]);
			}
			//#warning-ts: should log the else branch somehow
			
			return { 
				isLoggedIn: false, 
				userId: "",
				tenantId: "",
				fullName: "",
				upn: "",
				jwt: "",
				sessionExpiryTimestamp: 0,
				errorMessage: error?.message ?? error 
			};
		}
	};

	const trySignIn = async (isRetry: boolean, token: string, tenantId: any) => {
		let response = await signInAzureAD(
			token,
			tenantId,
			JSON.stringify(debugInfoStore)
		);
		debugInfoActions.removeDebugInfoItems([
			DebugInfoKey.WindowReloadTriggerEvents,
		]);
		const validations = response.validationFailures;

		const cachedDeploymentKey = localStorage.getItem(
			StorageKeys.DeploymentTenantKey
		);

		if (cachedDeploymentKey) {
			document.cookie = `env=${cachedDeploymentKey?.split(":")[0]}; max-age=60480000; SameSite=None; Secure; Partitioned;`;
		}

		if (
			!isRetry &&
			response.result === ResponseStatus.SignInWrongEnvironment &&
			!!validations
		) {
			const tenantDeployments = response.data?.deployments;
			if (
				tenantDeployments &&
				tenantDeployments.length === 1 &&
				tenantDeployments[0]
			) {
				if (
					cachedDeploymentKey !==
					`${tenantDeployments[0].deployment.id}:${tenantDeployments[0].tenantId}:${tenantDeployments[0].azureTenantId}`
				) {
					resetTenantDeploymentCache(tenantDeployments[0]);
				}

				response = await trySignIn(true, token, tenantId);
			}
		}

		return response;
	};

	const resetTenantDeploymentCache = async (tenantDeployment: any) => {
		localStorage.setItem(
			StorageKeys.DeploymentTenantKey,
			`${tenantDeployment.deployment.id}:${tenantDeployment.tenantId}:${tenantDeployment.azureTenantId}`
		);

		localStorage.setItem(
			StorageKeys.DeploymentTenantId,
			tenantDeployment.tenantId
		);

		document.cookie = `env=${tenantDeployment.deployment.id}; max-age=60480000; SameSite=None; Secure; Partitioned;`;
	};

	const contextData = useMemo<AuthContextType>(
		() => ({
			...authState,
			signOut: signOutCallback,
			azureSignIn: azureSignIn,
			msalSignIn: msalSignIn,
		}),
		[authState, signOutCallback, azureSignIn]
	);

	return (
		<AuthContext.Provider value={contextData}>
			{props.children}
		</AuthContext.Provider>
	);
};
